import React, { Component } from 'react';
import CustomSidebarLayout from '../../custom/CustomSidebarLayout';
import { RootState } from '../../../redux/store';
import {
  changeChart,
  changeChartEditorOptions,
  changeChartResponse,
  changeIsUpdate,
  changeLoading,
  clearChart,
  setExpressionVariables,
} from '../../../redux/actions/ChartActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import AxisTab from '../control-panel/AxisTab';
import SegmentContainer from '../control-panel/segments/SegmentContainer';
import MetricContainer from '../control-panel/metrics/MetricContainer';
import { Chart, ChartJsonModel, MetricItem, SegmentItem } from '../../../domain/chart/ChartDomains';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { ChartState } from '../../../redux/reducers/ChartReducer';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CollectionNames from '../../../constant/CollectionNames';
import CustomLoading from '../../custom/CustomLoading';
import GlobalFilterContainer from '../control-panel/filter/GlobalFilterContainer';
import MessageUtil from '../../../util/MessageUtil';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { PageState } from '../../../redux/reducers/PageReducer';
import ChartSettingsContainer from './ChartSettingsContainer';
import ChartInformationComponent from './ChartInformationComponent';
import ChartService from '../../../service/ChartService';
import ChartValueFactory from '../../../util/ChartValueFactory';
import { TransactionJsonModel, TransactionVariableJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import DataType from '../../../constant/DataType';
import ChartJSComponent from '../../dashboard/ChartJSComponent';
import { ChartType, TokenVariable } from '@trlogic/ui-pack';
import { Dashboard } from '../../../domain/dashboard/DashboardDomains';
import { FilterSegmentItem } from '../../../domain/chart/editor/Segment';
import VariableType from '../../../constant/VariableType';
import { AuthState } from '../../../redux/reducers/AuthReducer';
import { FormicaTimePickerDate } from '../../custom/custom-time-picker/TimePickerDomain';
import ChartUtil, { ChartUtilSettings } from '../../../util/ChartUtil';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import ExpressionBridge from '../../../util/ExpressionBridge';
import LanguageUtil from '../../../util/LanguageUtil';

class ChartEditorContainer extends Component<Props, State> {
  public state = new State();

  private computation: subscribe.Computation | undefined;
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  private transactionComputation: subscribe.Computation | undefined;
  private transactionSubscription: subscribe.Subscription | undefined;

  private chartSubscription: subscribe.Subscription | undefined;
  private dashboardSubscription: subscribe.Subscription | undefined;

  private chartService: ChartService = ServiceContainer.getInstance().get(ServiceNames.CHART);
  private readonly expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);

  render() {
    const {
      chart: { chart, chartEditorOptions },
    } = this.props;
    const { chartVersion } = this.state;
    return (
      <div className={'flex flex-col w-full h-full'}>
        <ChartInformationComponent chartVersion={chartVersion} setChartVersion={(version: number) => this.setState({ chartVersion: version })} />
        <ChartSettingsContainer simulateChart={this.simulateChart} />
        <div className={'flex xl:flex-row flex-col w-full h-full gap-x-5 gap-y-5 px-5 py-2'}>
          <div
            id={'chart-container'}
            className={'flex flex-col max-h-[calc(100dvh-240px)] w-full  overflow-hidden items-center overflow-x-scroll justify-start border rounded-lg'}
          >
            <div className={'flex w-full shrink-0 box-border items-center h-[51px] bg-[#F7F8FA]'}>
              {chart.name && <p className={'text-primary text-[14px] font-medium px-3'}>{chart.name}</p>}
            </div>
            {this.getChart()}
          </div>
          <div
            className={`opacity-100 xl:w-[387px] xl:min-w-[500px] flex shrink-0 flex-col transition-all  w-ful ease-in-out relative h-full rounded-lg border`}
          >
            <div className={'flex items-center px-5 rounded-t-lg justify-between w-full min-h-[51px] h-[51px] m-0 p-0 bg-[#F7F8FA]'}>
              <p className={'text-primary text-[14px] font-medium'}>{LanguageUtil.get({ id: 'common.label.controlPanel', defaultMessage: 'Control Panel' })}</p>
            </div>
            {this.state.loading ? (
              <CustomLoading />
            ) : (
              <div className={'flex min-h-full flex-col py-2'}>
                <AxisTab />
                <CustomSidebarLayout
                  title={LanguageUtil.get({ id: 'common.label.segment', defaultMessage: 'Segment' })}
                  icon={'horizontal'}
                  content={
                    <SegmentContainer
                      isBreakdown={false}
                      chartType={chart.type || ChartType.LINE}
                      onChangeSegmentItem={this.onChangeSegmentItem}
                      segmentItem={chartEditorOptions.segmentItem}
                    />
                  }
                  visible={chartEditorOptions.segmentEditorVisible}
                  onHide={() => this.toggleSegmentEditor()}
                  closable
                />
                <CustomSidebarLayout
                  title={LanguageUtil.get({ id: 'common.label.metric', defaultMessage: 'Metric' })}
                  icon={'vertical'}
                  closable
                  content={<MetricContainer />}
                  visible={chartEditorOptions.metricEditorVisible}
                  onHide={() => this.toggleMetricEditor()}
                />
                <CustomSidebarLayout
                  visible={chartEditorOptions.globalFilterEditorVisible}
                  icon={'filter'}
                  closable
                  onHide={() => this.toggleFilterEditor()}
                  title={LanguageUtil.get({ id: 'common.label.metric', defaultMessage: 'Metric' })}
                  content={<GlobalFilterContainer />}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    const { leaveComponent, clearChart } = this.props;
    leaveComponent();
    clearChart();
    await this.stopTracker();
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const { isUpdate } = this.props;
    if (isUpdate != prevProps.isUpdate) {
      this.resubscribeTracker();
    }
  }

  private resubscribeTracker = async () => {
    if (this.computation == undefined) {
      this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private startTracker = async () => {
    const { router } = this.props;
    const { chartId, dashboard } = router.params;
    if (!chartId && !dashboard) return;

    const chartQuery: query.SelectQueryBuilder = QueryBuilder.select().from(CollectionNames.CHARTS).where(eq('id', chartId));
    const dashboardQuery: query.SelectQueryBuilder = QueryBuilder.select().from(CollectionNames.DASHBOARDS).where(eq('id', dashboard));

    this.chartSubscription = subscribe.Formica.subscribe(CollectionNames.CHARTS, chartQuery.build());
    this.dashboardSubscription = subscribe.Formica.subscribe(CollectionNames.DASHBOARDS, dashboardQuery.build());

    this.computation = subscribe.Tracker.autorun(this.tracker, [this.chartSubscription, this.dashboardSubscription]);
  };

  private startTransactionTracker = async () => {
    const { router } = this.props;
    const { chartId, dashboard } = router.params;
    if (!chartId && !dashboard) return;

    const _dashboard: Pick<Dashboard, 'name' | 'selectedTransaction'> | undefined = await this.repository.find(
      query.QueryBuilder.select().from(CollectionNames.DASHBOARDS).where(eq('id', dashboard)).select('name', 'selectedTransaction').build(),
    );
    if (!_dashboard) {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'messages.warning.warning', defaultMessage: 'Warning' }),
        content: LanguageUtil.get({ id: 'messages.warning.dashboardNotFound' }),
      });
      return;
    }
    const transactionQuery: query.SelectQueryBuilder = QueryBuilder.select()
      .from(CollectionNames.TRANSACTIONS)
      .where(eq('name', _dashboard.selectedTransaction));
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.transactionComputation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription]);
  };

  private tracker = async () => {
    const {
      changeIsUpdate,
      changeChartEditorOptions,
      changeChart,
      chart: { chart, chartEditorOptions },
      setExpressionVariables,
    } = this.props;
    if (this.chartSubscription?.ready() && this.dashboardSubscription?.ready()) {
      if (this.transactionComputation == undefined) {
        this.startTransactionTracker();
        return;
      } else if (!this.transactionSubscription?.ready()) {
        return;
      }
      const { chartId, dashboard } = this.props.router.params;

      const dashboardQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.DASHBOARDS).where(eq('id', dashboard)).build();
      const _dashboard: Dashboard | undefined = await this.repository.find(dashboardQuery);
      const chartQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.CHARTS).where(eq('id', chartId)).build();

      const _chart: ChartJsonModel | undefined = await this.repository.find(chartQuery);
      chart.dataSource = _dashboard?.selectedTransaction as string;

      const transactionQuery: query.Query = query.QueryBuilder.select()
        .from(CollectionNames.TRANSACTIONS)
        .select('name', 'variables')
        .where(eq('name', _dashboard?.selectedTransaction))
        .build();
      const transaction: TransactionJsonModel | undefined = await this.repository.find(transactionQuery);
      this.setState({ transaction: transaction }, async () => {
        this.prepareTransactionVariables();
        const variables = await this.prepareVariables();
        await this.expressionBridge.prepareVariables(variables);
        setExpressionVariables(variables);
      });

      if (_chart == undefined || chartId == undefined) {
        changeIsUpdate(false);
        this.setState({ loading: false });
        return;
      }

      chartEditorOptions.metricItem = _chart.chartEditorOptions.metricItem as MetricItem[];
      if (chartEditorOptions?.metricItem?.length > 0) {
        chartEditorOptions.activeMetricItem = chartEditorOptions.metricItem[0];
      }
      chartEditorOptions.segmentItem = _chart.chartEditorOptions.segmentItem as SegmentItem;
      chartEditorOptions.breakdown = _chart.chartEditorOptions.breakdown as SegmentItem;
      chartEditorOptions.globalFilterItem = _chart.chartEditorOptions?.globalFilterItem as FilterSegmentItem;
      chartEditorOptions.endDate = _dashboard?.endDate as FormicaTimePickerDate;
      chartEditorOptions.startDate = _dashboard?.startDate as FormicaTimePickerDate;
      chart.visualOptions = _chart.visualOptions;
      chart.id = _chart.id;
      chart.type = _chart.type as ChartType;
      chart.name = _chart.name;
      chart.description = _chart.description;
      chart.createdDate = _chart.createdDate;
      chart.updatedDate = _chart.updatedDate;
      chart.dashboardId = _dashboard?.name as string;
      changeChart(chart);
      changeChartEditorOptions(chartEditorOptions);
      changeIsUpdate(true);
      this.getChartOptions();
      await this.executeChart();
      this.setState({ loading: false });
    }
  };

  private getChartOptions = () => {
    const {
      chart: { chart, chartEditorOptions },
      changeChart,
    } = this.props;
    const chartSettings: ChartUtilSettings = {
      name: chart.name || LanguageUtil.get({ id: 'common.label.testChart', defaultMessage: 'Test Chart' }),
      metrics: chartEditorOptions.metricItem,
      legendDisplay: true,
    };
    const options = ChartUtil.getChartOptionsByChartType(chart.type || ChartType.BAR_VERTICAL_ROWED, chartSettings);
    if (chart.visualOptions) {
      (chart.visualOptions as any)['plugins']['tooltip'] = options.plugins?.tooltip;
      chart.visualOptions.scales = options.scales;
      chart.visualOptions.indexAxis = options.indexAxis;
    } else {
      chart.visualOptions = options;
    }
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    await this.chartSubscription?.stop();
    await this.dashboardSubscription?.stop();
    this.computation?.stop();
  };

  private prepareVariables = async (): Promise<Record<string, TokenVariable[]>> => {
    const {
      chart: { chart },
    } = this.props;
    const { transaction } = this.state;
    if (chart.dataSource == undefined || transaction == undefined) return {};
    const eventVariables: any[] = transaction.variables.map((v) => ({
      type: VariableType.EVENT_VARIABLE as any,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.EVENT_VARIABLE as any,
        properties: v.properties,
        name: v.name,
        args: {
          event: transaction.name,
        },
      },
    }));
    eventVariables.push(...ChartValueFactory.initializeElasticVariables(this.props?.auth?.user?.activeRiskType as string, transaction.name as string));
    return {
      [VariableType.EVENT_VARIABLE]: eventVariables,
    };
  };

  private onChangeSegmentItem = (segmentItem: SegmentItem) => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    chartEditorOptions.segmentItem = segmentItem;
    changeChartEditorOptions(chartEditorOptions);
  };

  private getChart = () => {
    const {
      chart: { chart, chartEditorOptions, chartResponse },
    } = this.props;
    const { chartVersion, loading, error } = this.state;
    if (error) return <div>{LanguageUtil.get({ id: 'messages.error.error' })}</div>;
    if (loading) return <CustomLoading />;
    return (
      <ChartJSComponent
        metrics={chartEditorOptions.metricItem}
        key={chartVersion}
        segmentItem={chartEditorOptions?.segmentItem as any}
        options={chart.visualOptions}
        chartType={chart.type as ChartType}
        data={chartResponse}
        chartName={chart.name as string}
      />
    );
  };

  private simulateChart = async () => {
    const {
      chart: { chart, chartEditorOptions },
      changeChartResponse,
      changeLoading,
    } = this.props;
    const { chartVersion } = this.state;
    this.setState({ loading: true, error: false });
    const response = await this.chartService.simulate(chart as Chart, chartEditorOptions).catch((e) => {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.simulatingChart', defaultMessage: 'Error while simulating chart' }),
      });
      this.setState({ loading: false, error: true });
    });

    changeChartResponse(ChartUtil.setColorPalette(chartEditorOptions, response, chart.type as ChartType));
    this.setState({ chartVersion: chartVersion + 1 });
    this.setState({ loading: false });
    changeLoading(false);
  };
  private executeChart = async () => {
    const {
      chart: { chart, chartEditorOptions },
      changeChartResponse,
      changeLoading,
    } = this.props;
    const { chartVersion } = this.state;
    this.setState({ loading: true, error: false });
    const response = await this.chartService.execute(chart.id as string, chartEditorOptions, chart.type as ChartType).catch((e) => {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.error' }),
        content: e.message,
      });
      this.setState({ loading: false, error: true });
    });
    changeChartResponse(ChartUtil.setColorPalette(chartEditorOptions, response, chart.type as ChartType));
    this.setState({ chartVersion: chartVersion + 1 });
    this.setState({ loading: false });
    changeLoading(false);
  };

  private toggleSegmentEditor = () => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    chartEditorOptions.segmentEditorVisible = !chartEditorOptions.segmentEditorVisible;
    changeChartEditorOptions(chartEditorOptions);
  };
  private toggleFilterEditor = () => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    chartEditorOptions.globalFilterEditorVisible = !chartEditorOptions.globalFilterEditorVisible;
    changeChartEditorOptions(chartEditorOptions);
  };

  private toggleMetricEditor = () => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const items = chartEditorOptions.metricItem;
    if (items && items.length > 0) {
      if (items[0].metric.type == undefined || items[0].metric.field == undefined || items[0].metric.field.name == '') {
        items.splice(0, 1);
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.warning', defaultMessage: 'Warning' }),
          content: LanguageUtil.get({ id: 'messages.warning.selectFieldAndType', defaultMessage: 'Please select a field and type for the first item' }),
        });
      }
      // field veua type boş ise item'i sil
      items.map((item: MetricItem, index) => {
        if (item.metric.type == undefined || item.metric.field == undefined || item.metric.field.name == '') {
          items.splice(index, 1);
          MessageUtil.warning({
            summary: 'Warning',
            content: 'Please select a field and type for the metric item',
          });
        }
      });
    }
    chartEditorOptions.metricItem = items;
    chartEditorOptions.metricEditorVisible = !chartEditorOptions.metricEditorVisible;
    changeChartEditorOptions(chartEditorOptions);
  };

  private prepareTransactionVariables = (): void => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const { transaction } = this.state;
    const transactionVariables: FormicaSelectBoxItem[] = [];
    if (transaction) {
      transaction.variables.map((variable: TransactionVariableJsonModel) => {
        transactionVariables.push({
          label: variable.name,
          value: this.prepareVariableType(variable),
          icon: 'transaction',
          dataType: variable.properties.dataType,
        });
      });
    }
    chartEditorOptions.transactionVariables = transactionVariables;
    changeChartEditorOptions(chartEditorOptions);
  };

  private prepareVariableType = (variable: TransactionVariableJsonModel): string => {
    switch (variable.properties.dataType) {
      case DataType.DATE:
        return 'variables.' + variable.name;
      case DataType.STRING:
        return 'variables.' + variable.name + '.keyword';
      default:
        return 'variables.' + variable.name;
    }
  };
}

class State {
  loading: boolean = true;
  error: boolean = false;
  chartVersion: number = 0;
  transaction: TransactionJsonModel | undefined = undefined;
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  chart: state.chart as ChartState,
  isUpdate: state.chart.isUpdate,
  page: state.page as PageState,
  auth: state.auth as AuthState,
});

const mapDispatchToProps = {
  changeChart,
  changeChartEditorOptions,
  changeIsUpdate,
  enterComponent,
  leaveComponent,
  clearChart,
  changeLoading,
  setExpressionVariables,
  changeChartResponse,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ChartEditorContainer));
